import type { IWidgetSpan, TWidgetSizeKey, TWidgetTone } from "./types";

/** Layouts are authored against 12 columns and scaled down from there. */
export const BASE_COLUMNS = 12;

/** One grid row in px. Two rows + the gap is the tallest widget the top half fits. */
export const ROW_HEIGHT = 104;

/** Gap between cells, in px. Shared by the CSS grid and the resize math. */
export const GRID_GAP = 12;

/** Bumped when the stored document shape changes so an old layout can be migrated. */
export const LAYOUT_VERSION = 1;

/** Debounce before a layout edit is flushed to the API. */
export const LAYOUT_SAVE_DEBOUNCE_MS = 900;

/** How long the command bar holds the "Saved" confirmation. */
export const LAYOUT_SAVED_FLASH_MS = 2200;

/** How long a newly added widget keeps its arrival ring. */
export const ARRIVAL_CUE_MS = 2400;

/**
 * Hold time before a press on a widget opens customize mode. Matches the ~500ms
 * every touch platform has trained people to expect; shorter fires on taps,
 * longer reads as unresponsive.
 */
export const LONG_PRESS_MS = 500;

/** Movement past this cancels the press — it was a scroll, not a hold. */
export const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

/**
 * Ordered by area, then by height at a tie — `tall` (3×2, area 6) sits ahead of
 * `md` (6×1, also area 6) because a keyboard user stepping up from `sm` reaches
 * the same-width, taller neighbour before the wider, flatter one. Resize
 * stepping, the size picker and the drag-to-nearest-size search all read this
 * order, so it is the one place that has to get the "what's next" question
 * right for every widget in the system at once.
 */
export const WIDGET_SIZE_ORDER: TWidgetSizeKey[] = [
  "xs",
  "sm",
  "tall",
  "md",
  "lg",
  "xl",
  "grand",
];

/**
 * Footprint of each size against `BASE_COLUMNS`.
 *
 * `xs` is a sixth of the row rather than a quarter, so it reads as a distinct
 * step down from `sm` instead of the same tile in a slightly smaller box — at
 * full width that is roughly 160px versus 240px, close to how much smaller an
 * iOS "small" widget is next to its "medium" sibling.
 *
 * `tall` and `grand` are the two places height moves without width: `tall` is
 * `sm` stood up (3×2), `grand` is `xl` stretched one row deeper (12×3) — the
 * only two spans in the table that are not a plain multiple of an existing one,
 * which is deliberate: without them every resize step changed width, and
 * dragging a card's bottom edge had nothing distinct to land on.
 */
export const SIZE_SPANS: Record<TWidgetSizeKey, IWidgetSpan> = {
  xs: { w: 2, h: 1 },
  sm: { w: 3, h: 1 },
  tall: { w: 3, h: 2 },
  md: { w: 6, h: 1 },
  lg: { w: 6, h: 2 },
  xl: { w: 12, h: 2 },
  grand: { w: 12, h: 3 },
};

/** Tallest span in the system, in rows — the scale drawings size their outline against it. */
export const TALLEST_ROWS = Math.max(
  ...Object.values(SIZE_SPANS).map((span) => span.h),
);

/** Short labels for the size picker, the resize preview and screen readers.
 *  Lookup KEYS — a module-scope Record cannot call a hook; consumers use `t()`. */
export const SIZE_LABELS: Record<TWidgetSizeKey, string> = {
  xs: "common:dashboard_grid.size_xs",
  sm: "common:dashboard_grid.size_sm",
  tall: "common:dashboard_grid.size_tall",
  md: "common:dashboard_grid.size_md",
  lg: "common:dashboard_grid.size_lg",
  xl: "common:dashboard_grid.size_xl",
  grand: "common:dashboard_grid.size_grand",
};

/**
 * Tone → the token the card's corner accent and icon tile are mixed from.
 * A var name rather than a class, because `--widget-accent` is consumed by the
 * `color-mix()` in `.widget-card::after` and cannot come from a utility class.
 */
export const TONE_ACCENT_VAR: Record<TWidgetTone, string> = {
  primary: "var(--color-primary)",
  info: "var(--color-info)",
  success: "var(--color-success)",
  warning: "var(--color-warning)",
  danger: "var(--color-danger)",
};

/**
 * Glyph colour for the app-icon chip, whose ground is the accent at full
 * strength. Taken from each tone's own `-foreground` token rather than assumed
 * to be white: the design system already decided what is legible on `warning`,
 * and that is not a call to re-make in a gradient.
 */
export const TONE_ACCENT_FG_VAR: Record<TWidgetTone, string> = {
  primary: "var(--color-primary-foreground)",
  info: "var(--color-info-foreground)",
  success: "var(--color-success-foreground)",
  warning: "var(--color-warning-foreground)",
  danger: "var(--color-danger-foreground)",
};

/**
 * Column count by container width. Measured on the canvas rather than the
 * viewport: the sidebar and the split-screen panel both change how much room the
 * dashboard actually has, and a viewport media query would miss both.
 */
export const COLUMN_BREAKPOINTS: { minWidth: number; columns: number }[] = [
  { minWidth: 1180, columns: 12 },
  { minWidth: 900, columns: 8 },
  { minWidth: 620, columns: 6 },
  { minWidth: 420, columns: 4 },
  { minWidth: 0, columns: 2 },
];

/**
 * At or below this many columns every widget goes full width — a quarter of a
 * 380px canvas is not a card, it is a sliver.
 */
export const STACK_COLUMN_THRESHOLD = 2;
